import React, { useEffect, useState, useRef } from "react";
import { Plus, Minus, Edit3, Eye, EyeOff, Save, X, RefreshCw, Search, Tag, Calendar, Copy, Trash2, ImageIcon, Upload, Star, AlertTriangle, ChevronUp, ChevronDown, Sparkles, Wand2 } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { ModalPortal } from "./ModalPortal";
import { ProductImage } from "./ProductImage";
import { ProductImageEditor } from "./ProductImageEditor";

const TEXT_AREA_FIELDS = ["short_description", "description", "research_overview", "storage"];

const FIELD_ORDER = [
    { k: "slug", label: "URL slug (lowercase, hyphens, unique)" },
    { k: "name", label: "Name" },
    { k: "category", label: "Category" },
    { k: "dosage", label: "Dosage" },
    { k: "sku", label: "SKU" },
    { k: "price", label: "Price (AUD) — RRP", type: "number" },
    { k: "sale_price", label: "Sale price (AUD) — leave blank for none", type: "number" },
    { k: "sale_starts_at", label: "Sale starts (optional)", type: "datetime" },
    { k: "sale_ends_at", label: "Sale ends (optional)", type: "datetime" },
    { k: "stock", label: "Stock", type: "number" },
    { k: "sort_order", label: "Sort order (lower = shown first)", type: "number" },
    { k: "lot", label: "Batch / Lot" },
    { k: "purity", label: "Purity" },
    { k: "image", label: "Primary image URL (auto-set by upload)" },
    { k: "short_description", label: "Short description" },
    { k: "description", label: "Long description" },
    { k: "research_overview", label: "Research overview" },
    { k: "storage", label: "Storage instructions" },
    { k: "benefits", label: "Benefits (one per line)", type: "list" },
    { k: "is_new", label: "Mark as NEW", type: "bool" },
    { k: "is_bestseller", label: "Mark as BEST SELLER", type: "bool" },
    { k: "active", label: "Active (visible in shop)", type: "bool" },
];

// Sentinel used in `editing` state to indicate the "new product" mode.
const NEW_PRODUCT_KEY = "__new__";

const emptyProductForm = () => ({
    slug: "",
    name: "",
    category: "",
    dosage: "",
    sku: "",
    price: "",
    sale_price: "",
    sale_starts_at: "",
    sale_ends_at: "",
    stock: 0,
    sort_order: "",
    lot: "",
    purity: "",
    image: "",
    short_description: "",
    description: "",
    research_overview: "",
    storage: "",
    benefits: "",
    is_new: false,
    is_bestseller: false,
    active: true,
    // Pre-order (flattened for form binding; recombined on save)
    preorder_enabled: false,
    preorder_dispatch: "",
    preorder_arrival: "",
    preorder_max: "",  // "" = unlimited
    preorder_message: "",
    // Image display controls
    image_fit: "contain",
    image_zoom: 1.0,
    image_offset_x: 50,
    image_offset_y: 50,
});

const toLocalDtInput = (iso) => {
    if (!iso) return "";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "";
    const off = d.getTimezoneOffset();
    return new Date(d.getTime() - off * 60000).toISOString().slice(0, 16);
};
const toISO = (local) => (local ? new Date(local).toISOString() : null);

export const AdminProductsPanel = ({ token }) => {
    const [products, setProducts] = useState([]);
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(false);
    const [filter, setFilter] = useState("");
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState({});
    const [saving, setSaving] = useState(false);
    const [selected, setSelected] = useState(new Set());
    const [showSale, setShowSale] = useState(false);
    const [saleForm, setSaleForm] = useState({ sale_price: "", sale_starts_at: "", sale_ends_at: "" });
    const [saleOnly, setSaleOnly] = useState(false);
    const [regen, setRegen] = useState(null); // { slug, name, cap_colour, contents, extra_notes, submitting }
    const [editor, setEditor] = useState(null); // product object for Nano Banana editor
    const headers = { "X-Admin-Token": token };

    const load = async () => {
        setLoading(true);
        try {
            const r = await api.get("/admin/products", { headers });
            setProducts(r.data);
        } catch {
            toast.error("Failed to load products");
        }
        setLoading(false);
    };

    useEffect(() => {
        load();  /* eslint-disable-next-line */
        // Categories drive the product edit-form dropdown so admins can only
        // pick from the current live list. Public endpoint — no token needed.
        (async () => {
            try {
                const r = await api.get("/categories");
                setCategories(r.data || []);
            } catch { /* non-critical: falls back to free-text input */ }
        })();
    }, []);

    const adjustStock = async (slug, delta) => {
        const p = products.find((x) => x.slug === slug);
        if (!p) return;
        const newStock = Math.max(0, (p.stock || 0) + delta);
        try {
            const r = await api.patch(`/admin/products/${slug}`, { stock: newStock }, { headers });
            setProducts((arr) => arr.map((x) => (x.slug === slug ? r.data : x)));
        } catch {
            toast.error("Stock update failed");
        }
    };

    const setStockDirect = async (slug, value) => {
        const n = Math.max(0, parseInt(value, 10) || 0);
        try {
            const r = await api.patch(`/admin/products/${slug}`, { stock: n }, { headers });
            setProducts((arr) => arr.map((x) => (x.slug === slug ? r.data : x)));
        } catch {
            toast.error("Stock update failed");
        }
    };

    const toggleActive = async (p) => {
        const newActive = !(p.active !== false);
        try {
            const r = await api.patch(`/admin/products/${p.slug}`, { active: newActive }, { headers });
            setProducts((arr) => arr.map((x) => (x.slug === p.slug ? r.data : x)));
            toast.success(newActive ? "Listed in shop" : "Hidden from shop");
        } catch {
            toast.error("Toggle failed");
        }
    };

    /* Reorder — swap two products' positions in the list and persist via /admin/products/reorder. */
    const moveProduct = async (slug, dir) => {
        const idx = products.findIndex((x) => x.slug === slug);
        const j = idx + dir;
        if (idx < 0 || j < 0 || j >= products.length) return;
        const next = [...products];
        [next[idx], next[j]] = [next[j], next[idx]];
        setProducts(next);
        try {
            await api.post("/admin/products/reorder", { slugs: next.map((p) => p.slug) }, { headers });
        } catch {
            toast.error("Reorder failed");
            load(); // revert
        }
    };

    const [confirmDelete, setConfirmDelete] = useState(null);
    const duplicateProduct = async (slug) => {
        try {
            const r = await api.post(`/admin/products/${slug}/duplicate`, {}, { headers });
            toast.success(`Duplicated → ${r.data.slug}`);
            load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Duplicate failed");
        }
    };
    const hardDeleteProduct = async (slug) => {
        try {
            await api.delete(`/admin/products/${slug}/hard`, { headers });
            toast.success("Product permanently deleted");
            setConfirmDelete(null);
            load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Delete failed");
        }
    };

    const openEdit = (p) => {
        setEditing(p.slug);
        const f = { ...p };
        f.benefits = (p.benefits || []).join("\n");
        f.sale_starts_at = toLocalDtInput(p.sale_starts_at);
        f.sale_ends_at = toLocalDtInput(p.sale_ends_at);
        // Flatten pre_order for the form
        const po = p.pre_order || {};
        f.preorder_enabled = !!po.enabled;
        f.preorder_dispatch = po.estimated_dispatch_date || "";
        f.preorder_arrival = po.expected_arrival_date || "";
        f.preorder_max = (po.max_quantity === null || po.max_quantity === undefined) ? "" : po.max_quantity;
        f.preorder_message = po.message || "";
        // Image display controls — sensible defaults
        f.image_fit = p.image_fit || "contain";
        f.image_zoom = typeof p.image_zoom === "number" ? p.image_zoom : 1.0;
        f.image_offset_x = typeof p.image_offset_x === "number" ? p.image_offset_x : 50;
        f.image_offset_y = typeof p.image_offset_y === "number" ? p.image_offset_y : 50;
        setForm(f);
    };

    const openNew = () => {
        setEditing(NEW_PRODUCT_KEY);
        setForm(emptyProductForm());
    };

    /* Regenerate product photo using KLOW as the master template. */
    const openRegen = (p) => setRegen({
        slug: p.slug,
        name: p.name,
        cap_colour: "matte black",
        contents: "fine white lyophilised powder filling the bottom ~30% of the vial",
        vial_glass: "amber pharmaceutical glass",
        crimp: "brushed silver aluminium crimp collar",
        extra_notes: "",
        submitting: false,
        backups: [],
        loadingBackups: false,
    });

    const loadRegenBackups = async () => {
        if (!regen) return;
        setRegen((r) => ({ ...r, loadingBackups: true }));
        try {
            const { data } = await api.get(`/admin/products/${regen.slug}/image-backups`, { headers });
            setRegen((r) => (r ? { ...r, backups: data.backups || [], loadingBackups: false } : r));
        } catch {
            setRegen((r) => (r ? { ...r, loadingBackups: false } : r));
        }
    };

    const restoreBackup = async (index) => {
        if (!regen) return;
        setRegen((r) => ({ ...r, submitting: true }));
        try {
            const { data } = await api.post(`/admin/products/${regen.slug}/restore-image`, { index }, { headers });
            const url = data.public_url;
            setProducts((arr) => arr.map((x) => (x.slug === regen.slug ? { ...(data.product || x), image: url } : x)));
            if (editing === regen.slug) setForm((f) => ({ ...f, image: url }));
            toast.success(`Restored previous version`);
            setRegen(null);
        } catch (err) {
            const detail = err?.response?.data?.detail;
            toast.error(typeof detail === "string" ? detail : "Restore failed");
            setRegen((r) => (r ? { ...r, submitting: false } : r));
        }
    };

    const submitRegen = async () => {
        if (!regen) return;
        setRegen((r) => ({ ...r, submitting: true }));
        try {
            const body = {
                cap_colour: regen.cap_colour.trim() || "matte black",
                contents: regen.contents.trim(),
                vial_glass: regen.vial_glass.trim() || "amber pharmaceutical glass",
                crimp: regen.crimp.trim() || "brushed silver aluminium crimp collar",
                extra_notes: regen.extra_notes.trim() || null,
            };
            const { data } = await api.post(`/admin/products/${regen.slug}/regenerate-image`, body, { headers });
            const url = data.public_url;
            setProducts((arr) => arr.map((x) => (x.slug === regen.slug ? { ...(data.product || x), image: url } : x)));
            if (editing === regen.slug) {
                setForm((f) => ({ ...f, image: url }));
            }
            toast.success("Photo regenerated");
            setRegen(null);
        } catch (err) {
            const detail = err?.response?.data?.detail;
            toast.error(typeof detail === "string" ? detail : "Regeneration failed");
            setRegen((r) => (r ? { ...r, submitting: false } : r));
        }
    };

    const toggleSelect = (slug) => {
        setSelected((prev) => {
            const next = new Set(prev);
            if (next.has(slug)) next.delete(slug);
            else next.add(slug);
            return next;
        });
    };

    const clearSelection = () => setSelected(new Set());

    const applyBulkSale = async () => {
        const slugs = Array.from(selected);
        if (slugs.length === 0) return;
        const sp = Number(saleForm.sale_price);
        if (!sp || sp <= 0) {
            toast.error("Sale price must be greater than 0");
            return;
        }
        try {
            await api.post("/admin/products/sale", {
                slugs,
                sale_price: sp,
                sale_starts_at: toISO(saleForm.sale_starts_at),
                sale_ends_at: toISO(saleForm.sale_ends_at),
            }, { headers });
            toast.success(`Put ${slugs.length} product${slugs.length === 1 ? "" : "s"} on sale`);
            setShowSale(false);
            setSaleForm({ sale_price: "", sale_starts_at: "", sale_ends_at: "" });
            clearSelection();
            await load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Bulk sale failed");
        }
    };

    const clearBulkSale = async () => {
        const slugs = Array.from(selected);
        if (slugs.length === 0) return;
        if (!window.confirm(`Remove sale pricing from ${slugs.length} product${slugs.length === 1 ? "" : "s"}?`)) return;
        try {
            await api.post("/admin/products/sale/clear", { slugs }, { headers });
            toast.success(`Removed sale from ${slugs.length} product${slugs.length === 1 ? "" : "s"}`);
            clearSelection();
            await load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Bulk clear failed");
        }
    };

    const saveEdit = async () => {
        if (!editing) return;
        const isNew = editing === NEW_PRODUCT_KEY;
        setSaving(true);
        const payload = {};
        for (const { k, type } of FIELD_ORDER) {
            const v = form[k];
            if (v === undefined || v === null || v === "") continue;
            if (type === "list") {
                payload[k] = String(v).split("\n").map((s) => s.trim()).filter(Boolean);
            } else if (type === "number") {
                payload[k] = Number(v);
            } else if (type === "datetime") {
                payload[k] = toISO(v);
            } else {
                payload[k] = v;
            }
        }
        // For a brand-new product, ensure required fields for the ProductCreate schema
        if (isNew) {
            const missing = ["slug", "name", "category", "dosage", "price", "image", "short_description", "description"]
                .filter((k) => !payload[k] && payload[k] !== 0);
            if (missing.length) {
                toast.error(`Fill required: ${missing.join(", ")}`);
                setSaving(false);
                return;
            }
            payload.stock = payload.stock ?? 0;
            payload.active = payload.active !== false;
            payload.benefits = payload.benefits || [];
        }
        // If admin emptied sale_price on an existing product, send clear_sale=true to wipe the sale window
        if (!isNew && (form.sale_price === "" || form.sale_price === null || form.sale_price === undefined)) {
            payload.clear_sale = true;
        }
        // Image display controls — always send so admins can dial in
        // portrait uploads to visually match existing landscape listings.
        if (form.image_fit) payload.image_fit = form.image_fit;
        if (typeof form.image_zoom === "number") payload.image_zoom = form.image_zoom;
        if (typeof form.image_offset_x === "number") payload.image_offset_x = form.image_offset_x;
        if (typeof form.image_offset_y === "number") payload.image_offset_y = form.image_offset_y;

        // Pre-order block: only emit the object when the toggle is on. When
        // the toggle is off on an existing product, send clear_pre_order=true
        // so the field is removed cleanly. For a new product with the toggle
        // off, we simply omit the field so the product ships as standard.
        if (form.preorder_enabled) {
            payload.pre_order = {
                enabled: true,
                estimated_dispatch_date: (form.preorder_dispatch || "").trim() || null,
                expected_arrival_date: (form.preorder_arrival || "").trim() || null,
                max_quantity: (form.preorder_max === "" || form.preorder_max === null || form.preorder_max === undefined)
                    ? null
                    : Math.max(0, Number(form.preorder_max)),
                message: (form.preorder_message || "").trim() || null,
            };
        } else if (!isNew) {
            payload.clear_pre_order = true;
        }
        try {
            if (isNew) {
                await api.post("/admin/products", payload, { headers });
                toast.success(`${payload.name} created`);
                setEditing(null);
                await load();
            } else {
                const r = await api.patch(`/admin/products/${editing}`, payload, { headers });
                setProducts((arr) => arr.map((x) => (x.slug === editing ? r.data : x)));
                toast.success(`${r.data.name} updated`);
                setEditing(null);
            }
        } catch (err) {
            const detail = err?.response?.data?.detail;
            toast.error(Array.isArray(detail) ? detail.map((e) => e.msg).join(" ") : (detail || "Save failed"));
        }
        setSaving(false);
    };

    const filtered = products.filter((p) => {
        if (saleOnly && !p.on_sale) return false;
        if (!filter) return true;
        return (
            p.name.toLowerCase().includes(filter.toLowerCase()) ||
            p.slug.includes(filter.toLowerCase())
        );
    });

    return (
        <div className="space-y-4" data-testid="admin-products-panel">
            <div className="flex items-center gap-3 flex-wrap">
                <div className="flex items-center gap-2 glass rounded-full px-4 py-2 border border-white/10 flex-1 min-w-[260px]">
                    <Search size={14} className="text-[var(--text-muted)]" />
                    <input
                        type="text"
                        value={filter}
                        onChange={(e) => setFilter(e.target.value)}
                        placeholder="Search by name or slug…"
                        className="bg-transparent text-sm text-white flex-1 focus:outline-none"
                        data-testid="admin-products-filter"
                    />
                </div>
                <button
                    onClick={() => setSaleOnly((v) => !v)}
                    className={`px-3 py-2 rounded-full text-xs font-mono uppercase tracking-widest border inline-flex items-center gap-1 ${
                        saleOnly ? "border-[var(--gold)] text-[var(--gold)] bg-[rgba(233,188,11,0.08)]" : "border-white/10 text-[var(--text-secondary)] hover:border-white/30"
                    }`}
                    data-testid="admin-products-sale-filter"
                >
                    <Tag size={12} /> On sale
                </button>
                <button onClick={load} className="btn-neon" data-testid="admin-products-refresh">
                    <RefreshCw size={14} /> Refresh
                </button>
                <button onClick={openNew} className="btn-solid" data-testid="admin-product-new">
                    <Plus size={14} /> New product
                </button>
            </div>

            {/* Bulk action toolbar */}
            {selected.size > 0 && (
                <div
                    className="glass-elevated rounded-2xl p-3 flex items-center gap-3 flex-wrap border border-[var(--gold)]/30 bg-[rgba(233,188,11,0.05)]"
                    data-testid="admin-products-bulk-toolbar"
                >
                    <span className="text-sm text-white font-mono">{selected.size} selected</span>
                    <button
                        onClick={() => setShowSale((v) => !v)}
                        className="ml-auto px-3 py-1.5 rounded-full border border-[var(--gold)]/40 text-[var(--gold)] hover:bg-[var(--gold)]/10 text-xs font-mono uppercase tracking-widest inline-flex items-center gap-1"
                        data-testid="admin-products-bulk-sale-btn"
                    >
                        <Tag size={12} /> Put on sale
                    </button>
                    <button
                        onClick={clearBulkSale}
                        className="px-3 py-1.5 rounded-full border border-red-500/40 text-red-300 hover:bg-red-500/10 text-xs font-mono uppercase tracking-widest"
                        data-testid="admin-products-bulk-clear-btn"
                    >
                        Remove sale
                    </button>
                    <button
                        onClick={clearSelection}
                        className="px-3 py-1.5 rounded-full border border-white/10 text-[var(--text-secondary)] hover:border-white/30 text-xs font-mono uppercase tracking-widest"
                        data-testid="admin-products-bulk-cancel"
                    >
                        Cancel
                    </button>
                </div>
            )}

            {showSale && selected.size > 0 && (
                <div className="glass-elevated rounded-2xl p-4 space-y-3" data-testid="admin-products-bulk-sale-form">
                    <div className="grid md:grid-cols-3 gap-3">
                        <div>
                            <label className="text-[10px] font-mono uppercase tracking-widest text-[var(--text-muted)] mb-1 block">Sale price (AUD)</label>
                            <input
                                type="number"
                                step="0.01"
                                value={saleForm.sale_price}
                                onChange={(e) => setSaleForm({ ...saleForm, sale_price: e.target.value })}
                                placeholder="e.g. 80"
                                className="w-full px-4 py-2.5 bg-black/40 border border-white/10 rounded-full text-sm text-white placeholder:text-[var(--text-muted)] focus:outline-none focus:border-[var(--gold)]"
                                data-testid="bulk-sale-price"
                            />
                        </div>
                        <div>
                            <label className="text-[10px] font-mono uppercase tracking-widest text-[var(--text-muted)] mb-1 block">Starts (optional)</label>
                            <input
                                type="datetime-local"
                                value={saleForm.sale_starts_at}
                                onChange={(e) => setSaleForm({ ...saleForm, sale_starts_at: e.target.value })}
                                className="w-full px-4 py-2.5 bg-black/40 border border-white/10 rounded-full text-sm text-white focus:outline-none focus:border-[var(--gold)]"
                                data-testid="bulk-sale-starts"
                            />
                        </div>
                        <div>
                            <label className="text-[10px] font-mono uppercase tracking-widest text-[var(--text-muted)] mb-1 block">Ends (optional)</label>
                            <input
                                type="datetime-local"
                                value={saleForm.sale_ends_at}
                                onChange={(e) => setSaleForm({ ...saleForm, sale_ends_at: e.target.value })}
                                className="w-full px-4 py-2.5 bg-black/40 border border-white/10 rounded-full text-sm text-white focus:outline-none focus:border-[var(--gold)]"
                                data-testid="bulk-sale-ends"
                            />
                        </div>
                    </div>
                    <div className="flex gap-2">
                        <button onClick={applyBulkSale} className="btn-solid" data-testid="bulk-sale-apply">
                            <Tag size={14} /> Apply sale to {selected.size} product{selected.size === 1 ? "" : "s"}
                        </button>
                        <button onClick={() => setShowSale(false)} className="btn-ghost" data-testid="bulk-sale-cancel">Cancel</button>
                    </div>
                </div>
            )}

            {loading && <div className="text-[var(--text-secondary)]">Loading products…</div>}

            <div className="space-y-2">
                {filtered.map((p) => (
                    <div
                        key={p.slug}
                        className={`glass-elevated rounded-2xl p-4 ${
                            p.active === false ? "opacity-50" : ""
                        }`}
                        data-testid={`admin-product-${p.slug}`}
                    >
                        <div className="flex items-center gap-4 flex-wrap">
                            <input
                                type="checkbox"
                                checked={selected.has(p.slug)}
                                onChange={() => toggleSelect(p.slug)}
                                className="w-5 h-5 accent-[var(--gold)]"
                                data-testid={`admin-product-select-${p.slug}`}
                                aria-label={`Select ${p.name}`}
                            />
                            <div className="flex flex-col" title="Reorder">
                                <button
                                    onClick={() => moveProduct(p.slug, -1)}
                                    className="p-0.5 text-[var(--text-muted)] hover:text-white disabled:opacity-20"
                                    aria-label="Move product up"
                                    data-testid={`product-up-${p.slug}`}
                                >
                                    <ChevronUp size={12} />
                                </button>
                                <button
                                    onClick={() => moveProduct(p.slug, 1)}
                                    className="p-0.5 text-[var(--text-muted)] hover:text-white disabled:opacity-20"
                                    aria-label="Move product down"
                                    data-testid={`product-down-${p.slug}`}
                                >
                                    <ChevronDown size={12} />
                                </button>
                            </div>
                            <img
                                src={p.image}
                                alt={p.name}
                                className="w-24 h-16 object-contain rounded-lg"
                            />
                            <div className="flex-1 min-w-[200px]">
                                <div className="flex items-center gap-2 flex-wrap">
                                    <span className="font-display font-bold text-white">{p.name}</span>
                                    <span className="text-[10px] font-mono uppercase tracking-widest text-[var(--text-muted)]">
                                        {p.dosage}
                                    </span>
                                    {p.on_sale && (
                                        <span
                                            className="px-2 py-0.5 rounded-full text-[9px] font-mono uppercase tracking-wider border border-[var(--gold)]/40 text-[var(--gold)] inline-flex items-center gap-1"
                                            data-testid={`admin-product-sale-pill-${p.slug}`}
                                        >
                                            <Tag size={9} /> Sale −{p.discount_pct}%
                                        </span>
                                    )}
                                    {p.is_new && (
                                        <span className="px-2 py-0.5 rounded-full text-[9px] font-mono uppercase tracking-wider border border-emerald-500/40 text-emerald-300">
                                            New
                                        </span>
                                    )}
                                    {p.is_bestseller && (
                                        <span className="px-2 py-0.5 rounded-full text-[9px] font-mono uppercase tracking-wider border border-[var(--gold)]/40 text-[var(--gold)]">
                                            Bestseller
                                        </span>
                                    )}
                                    {p.active === false && (
                                        <span className="px-2 py-0.5 rounded-full text-[9px] font-mono uppercase tracking-wider border border-red-500/40 text-red-300">
                                            Hidden
                                        </span>
                                    )}
                                </div>
                                <div className="text-xs text-[var(--text-secondary)] mt-0.5">
                                    {p.category} ·{" "}
                                    {p.on_sale ? (
                                        <>
                                            <span className="text-[var(--gold)]">A${Number(p.sale_price).toFixed(2)}</span>{" "}
                                            <span className="line-through text-[var(--text-muted)]">A${Number(p.price).toFixed(2)}</span>
                                        </>
                                    ) : (
                                        <>A${Number(p.price).toFixed(2)}</>
                                    )}
                                    {" "}· <span className="font-mono">{p.lot}</span>
                                    {p.on_sale && p.sale_ends_at && (
                                        <span className="ml-2 inline-flex items-center gap-1 text-[10px] text-[var(--text-muted)]">
                                            <Calendar size={10} /> ends {new Date(p.sale_ends_at).toLocaleDateString("en-AU", { day: "numeric", month: "short" })}
                                        </span>
                                    )}
                                </div>
                            </div>

                            {/* Quick stock */}
                            <div className="flex items-center gap-1 glass rounded-full border border-white/10 px-1">
                                <button
                                    onClick={() => adjustStock(p.slug, -1)}
                                    className="p-1.5 text-white hover:text-[var(--gold)] disabled:opacity-30"
                                    disabled={(p.stock || 0) <= 0}
                                    aria-label="Decrement stock"
                                    data-testid={`stock-minus-${p.slug}`}
                                >
                                    <Minus size={14} />
                                </button>
                                <input
                                    type="number"
                                    value={p.stock || 0}
                                    onBlur={(e) => setStockDirect(p.slug, e.target.value)}
                                    onChange={(e) =>
                                        setProducts((arr) =>
                                            arr.map((x) =>
                                                x.slug === p.slug ? { ...x, stock: e.target.value } : x
                                            )
                                        )
                                    }
                                    className="w-14 px-2 py-1 bg-transparent text-center font-mono text-sm text-white focus:outline-none"
                                    data-testid={`stock-input-${p.slug}`}
                                />
                                <button
                                    onClick={() => adjustStock(p.slug, 1)}
                                    className="p-1.5 text-white hover:text-[var(--gold)]"
                                    aria-label="Increment stock"
                                    data-testid={`stock-plus-${p.slug}`}
                                >
                                    <Plus size={14} />
                                </button>
                            </div>

                            <button
                                onClick={() => toggleActive(p)}
                                className={`px-3 py-2 rounded-full text-xs font-mono uppercase tracking-widest border inline-flex items-center gap-1 ${
                                    p.active === false
                                        ? "border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10"
                                        : "border-white/10 text-[var(--text-secondary)] hover:border-red-500/40 hover:text-red-300"
                                }`}
                                data-testid={`toggle-active-${p.slug}`}
                                title={p.active === false ? "Show in shop" : "Hide from shop"}
                            >
                                {p.active === false ? <Eye size={12} /> : <EyeOff size={12} />}
                                {p.active === false ? "Show" : "Hide"}
                            </button>

                            <button
                                onClick={() => openEdit(p)}
                                className="px-3 py-2 rounded-full border border-[var(--gold)]/40 text-[var(--gold)] hover:bg-[var(--gold)]/10 text-xs font-mono uppercase tracking-widest inline-flex items-center gap-1"
                                data-testid={`edit-${p.slug}`}
                            >
                                <Edit3 size={12} /> Edit
                            </button>

                            <button
                                onClick={() => openRegen(p)}
                                className="px-3 py-2 rounded-full border border-[var(--tiffany)]/40 text-[var(--tiffany)] hover:bg-[var(--tiffany)]/10 text-xs font-mono uppercase tracking-widest inline-flex items-center gap-1"
                                data-testid={`regen-photo-${p.slug}`}
                                title="Regenerate photo (Nano Banana + KLOW template)"
                            >
                                <Sparkles size={12} /> Photo
                            </button>

                            <button
                                onClick={() => setEditor(p)}
                                className="px-3 py-2 rounded-full border border-[var(--gold)]/45 text-[var(--gold-hi)] hover:bg-[var(--gold)]/10 text-xs font-mono uppercase tracking-widest inline-flex items-center gap-1"
                                data-testid={`edit-image-${p.slug}`}
                                title="Open the general-purpose Product Image Editor"
                            >
                                <Wand2 size={12} /> Editor
                            </button>

                            <button
                                onClick={() => duplicateProduct(p.slug)}
                                className="px-3 py-2 rounded-full border border-white/10 text-[var(--text-secondary)] hover:border-white/25 hover:text-white text-xs font-mono uppercase tracking-widest inline-flex items-center gap-1"
                                data-testid={`duplicate-${p.slug}`}
                                title="Duplicate product"
                            >
                                <Copy size={12} /> Copy
                            </button>

                            <button
                                onClick={() => setConfirmDelete(p)}
                                className="px-3 py-2 rounded-full border border-red-500/40 text-red-300 hover:bg-red-500/10 text-xs font-mono uppercase tracking-widest inline-flex items-center gap-1"
                                data-testid={`delete-${p.slug}`}
                                title="Permanently delete"
                            >
                                <Trash2 size={12} /> Delete
                            </button>
                        </div>
                    </div>
                ))}
            </div>

            {/* Confirm delete modal */}
            {confirmDelete && (
                <ModalPortal>
                <div
                    className="fixed inset-0 z-[80] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
                    onClick={() => setConfirmDelete(null)}
                    data-testid="confirm-delete-modal"
                >
                    <div
                        className="glass-elevated rounded-2xl p-6 max-w-md w-full border border-red-500/30"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="flex items-center gap-3 mb-3">
                            <div className="w-10 h-10 rounded-full bg-red-500/15 border border-red-500/40 flex items-center justify-center">
                                <AlertTriangle size={18} className="text-red-300" />
                            </div>
                            <h3 className="font-display text-lg font-bold text-white">
                                Delete product?
                            </h3>
                        </div>
                        <p className="text-sm text-[var(--text-secondary)] mb-1">
                            <span className="text-white font-semibold">{confirmDelete.name}</span> ({confirmDelete.slug}) will be permanently removed.
                        </p>
                        <p className="text-xs text-[var(--text-muted)] mb-5">
                            Prefer &quot;Hide&quot; on the row if you may re-list later — Hide is reversible; Delete is not.
                        </p>
                        <div className="flex gap-2 justify-end">
                            <button
                                onClick={() => setConfirmDelete(null)}
                                className="px-4 py-2 rounded-full border border-white/10 text-xs font-mono uppercase tracking-widest text-[var(--text-secondary)] hover:border-white/25"
                                data-testid="confirm-delete-cancel"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={() => hardDeleteProduct(confirmDelete.slug)}
                                className="px-4 py-2 rounded-full bg-red-500/20 border border-red-500/50 text-red-200 hover:bg-red-500/30 text-xs font-mono uppercase tracking-widest inline-flex items-center gap-1"
                                data-testid="confirm-delete-confirm"
                            >
                                <Trash2 size={12} /> Delete forever
                            </button>
                        </div>
                    </div>
                </div>
                </ModalPortal>
            )}

            {/* Edit drawer */}
            {editing && (
                <ModalPortal>
                <div
                    className="fixed inset-0 z-[70] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
                    onClick={() => setEditing(null)}
                    data-testid="admin-edit-drawer"
                >
                    <div
                        className="glass-elevated rounded-2xl p-6 max-w-2xl w-full max-h-[88vh] overflow-y-auto border border-[var(--gold)]/30"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="flex items-center justify-between mb-5">
                            <h3 className="font-display text-xl font-bold text-white">
                                {editing === NEW_PRODUCT_KEY ? (
                                    <>New product</>
                                ) : (
                                    <>Edit · <span className="text-[var(--gold)]">{form.name}</span></>
                                )}
                            </h3>
                            <button
                                onClick={() => setEditing(null)}
                                className="p-2 text-[var(--text-muted)] hover:text-white"
                                aria-label="Close"
                                data-testid="admin-edit-close"
                            >
                                <X size={18} />
                            </button>
                        </div>

                        {editing === NEW_PRODUCT_KEY ? (
                            <p className="text-xs text-[var(--text-muted)] mb-4">
                                Fill the required fields (slug, name, category, dosage, price, image URL, short/long description) then click Create. Additional images can be uploaded after the product exists.
                            </p>
                        ) : (
                            <ProductImageManager
                                slug={editing}
                                initialImages={form.images || []}
                                initialPrimary={form.image}
                                headers={headers}
                                onChange={(images, primary) => setForm((f) => ({ ...f, images, image: primary }))}
                            />
                        )}

                        {/* Image display controls — how the product renders on
                            cards and PDPs. All four fields are optional and
                            fall back to the "Carbon Labs Product Listing
                            Standard" preset (contain, 1× zoom, centred). */}
                        {form.image && (
                            <ImageDisplayControls form={form} setForm={setForm} />
                        )}

                        <div className="space-y-3">
                            {FIELD_ORDER.map(({ k, label, type }) => {
                                const v = form[k];
                                if (type === "bool") {
                                    return (
                                        <label
                                            key={k}
                                            className="flex items-center justify-between p-3 rounded-xl glass border border-white/5 cursor-pointer"
                                        >
                                            <span className="text-sm text-white">{label}</span>
                                            <input
                                                type="checkbox"
                                                checked={!!v}
                                                onChange={(e) => setForm({ ...form, [k]: e.target.checked })}
                                                className="w-5 h-5 accent-[var(--gold)]"
                                                data-testid={`edit-${k}`}
                                            />
                                        </label>
                                    );
                                }
                                const isArea = TEXT_AREA_FIELDS.includes(k) || type === "list";
                                const isDatetime = type === "datetime";
                                const isCategory = k === "category";
                                return (
                                    <div key={k}>
                                        <label className="text-[10px] font-mono uppercase tracking-widest text-[var(--text-muted)] mb-1 block">
                                            {label}
                                        </label>
                                        {isCategory && categories.length > 0 ? (
                                            <select
                                                value={v || ""}
                                                onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                                                className="w-full px-3 py-2 bg-black/40 border border-white/10 rounded-full text-sm text-white focus:outline-none focus:border-[var(--gold)]"
                                                data-testid={`edit-${k}`}
                                            >
                                                <option value="">— Select a category —</option>
                                                {categories.map((c) => (
                                                    <option key={c.slug} value={c.slug}>
                                                        {c.name}
                                                    </option>
                                                ))}
                                            </select>
                                        ) : isArea ? (
                                            <textarea
                                                value={v || ""}
                                                onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                                                rows={k === "description" ? 5 : 3}
                                                className="w-full px-3 py-2 bg-black/40 border border-white/10 rounded-xl text-sm text-white focus:outline-none focus:border-[var(--gold)]"
                                                data-testid={`edit-${k}`}
                                            />
                                        ) : (
                                            <input
                                                type={isDatetime ? "datetime-local" : (type === "number" ? "number" : "text")}
                                                step={type === "number" && (k === "price" || k === "sale_price") ? "0.01" : "1"}
                                                value={v ?? ""}
                                                onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                                                className="w-full px-3 py-2 bg-black/40 border border-white/10 rounded-full text-sm text-white focus:outline-none focus:border-[var(--gold)]"
                                                data-testid={`edit-${k}`}
                                            />
                                        )}
                                    </div>
                                );
                            })}

                            {/* Pre-order configuration */}
                            <div className="mt-2 rounded-xl border border-[var(--gold)]/30 bg-[var(--gold)]/[0.03] p-4 space-y-3" data-testid="edit-preorder-section">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <div className="text-sm font-display font-bold text-[var(--gold)]">Pre-order</div>
                                        <div className="text-[11px] text-[var(--text-muted)] mt-0.5">
                                            When enabled, this product accepts orders even at stock=0. Reservations tick up against the configured allocation.
                                        </div>
                                    </div>
                                    <label className="inline-flex items-center gap-2 cursor-pointer shrink-0">
                                        <span className="text-[10px] font-mono uppercase tracking-widest text-[var(--text-muted)]">Enabled</span>
                                        <input
                                            type="checkbox"
                                            checked={!!form.preorder_enabled}
                                            onChange={(e) => setForm({ ...form, preorder_enabled: e.target.checked })}
                                            className="w-5 h-5 accent-[var(--gold)]"
                                            data-testid="edit-preorder-enabled"
                                        />
                                    </label>
                                </div>
                                {form.preorder_enabled && (
                                    <div className="space-y-3">
                                        <div>
                                            <label className="text-[10px] font-mono uppercase tracking-widest text-[var(--text-muted)] mb-1 block">
                                                Estimated dispatch date
                                            </label>
                                            <input
                                                type="date"
                                                value={form.preorder_dispatch || ""}
                                                onChange={(e) => setForm({ ...form, preorder_dispatch: e.target.value })}
                                                className="w-full px-3 py-2 bg-black/40 border border-white/10 rounded-full text-sm text-white focus:outline-none focus:border-[var(--gold)]"
                                                data-testid="edit-preorder-dispatch"
                                            />
                                        </div>
                                        <div>
                                            <label className="text-[10px] font-mono uppercase tracking-widest text-[var(--text-muted)] mb-1 block">
                                                Expected arrival with us (optional)
                                            </label>
                                            <input
                                                type="date"
                                                value={form.preorder_arrival || ""}
                                                onChange={(e) => setForm({ ...form, preorder_arrival: e.target.value })}
                                                className="w-full px-3 py-2 bg-black/40 border border-white/10 rounded-full text-sm text-white focus:outline-none focus:border-[var(--gold)]"
                                                data-testid="edit-preorder-arrival"
                                            />
                                        </div>
                                        <div>
                                            <label className="text-[10px] font-mono uppercase tracking-widest text-[var(--text-muted)] mb-1 block">
                                                Pre-order allocation (leave blank for unlimited)
                                            </label>
                                            <input
                                                type="number"
                                                min="0"
                                                value={form.preorder_max ?? ""}
                                                onChange={(e) => setForm({ ...form, preorder_max: e.target.value })}
                                                className="w-full px-3 py-2 bg-black/40 border border-white/10 rounded-full text-sm text-white focus:outline-none focus:border-[var(--gold)]"
                                                data-testid="edit-preorder-max"
                                            />
                                        </div>
                                        <div>
                                            <label className="text-[10px] font-mono uppercase tracking-widest text-[var(--text-muted)] mb-1 block">
                                                Pre-order message (optional — shown on the product page)
                                            </label>
                                            <input
                                                type="text"
                                                value={form.preorder_message || ""}
                                                onChange={(e) => setForm({ ...form, preorder_message: e.target.value })}
                                                placeholder="Expected dispatch from 15 September 2026"
                                                className="w-full px-3 py-2 bg-black/40 border border-white/10 rounded-full text-sm text-white focus:outline-none focus:border-[var(--gold)]"
                                                data-testid="edit-preorder-message"
                                            />
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="flex gap-3 mt-6">
                            <button
                                onClick={saveEdit}
                                disabled={saving}
                                className="btn-solid flex-1"
                                data-testid="admin-edit-save"
                            >
                                <Save size={14} /> {saving ? "Saving…" : (editing === NEW_PRODUCT_KEY ? "Create product" : "Save changes")}
                            </button>
                            <button
                                onClick={() => setEditing(null)}
                                className="btn-ghost"
                                data-testid="admin-edit-cancel"
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
                </ModalPortal>
            )}


            {/* General-purpose Product Image Editor (Nano Banana) */}
            {editor && (
                <ProductImageEditor
                    product={editor}
                    token={token}
                    onClose={() => setEditor(null)}
                    onImageUpdated={(url) => {
                        // Bust cache in the products list + edit form so admins
                        // see the fresh asset immediately without a full reload.
                        setProducts((arr) => arr.map((x) => (x.slug === editor.slug ? { ...x, image: url } : x)));
                        if (editing === editor.slug) setForm((f) => ({ ...f, image: url }));
                    }}
                />
            )}

            {/* Regenerate photo modal */}
            {regen && (
                <ModalPortal>
                    <div
                        className="fixed inset-0 z-[80] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
                        onClick={() => !regen.submitting && setRegen(null)}
                        data-testid="regen-photo-modal"
                    >
                        <div onClick={(e) => e.stopPropagation()} className="glass-elevated max-w-lg w-full rounded-2xl p-6 max-h-[90vh] overflow-y-auto">
                            <div className="flex items-center justify-between mb-4">
                                <h3 className="font-display text-lg font-bold text-white flex items-center gap-2">
                                    <Sparkles size={16} className="text-[var(--tiffany)]" />
                                    Regenerate photo · <span className="text-[var(--gold)]">{regen.name}</span>
                                </h3>
                                <button
                                    onClick={() => !regen.submitting && setRegen(null)}
                                    className="p-1.5 text-[var(--text-muted)] hover:text-white disabled:opacity-30"
                                    disabled={regen.submitting}
                                    aria-label="Close"
                                    data-testid="regen-photo-close"
                                >
                                    <X size={16} />
                                </button>
                            </div>
                            <p className="text-xs text-[var(--text-muted)] mb-4 leading-relaxed">
                                Uses KLOW as the master template. The product&apos;s current name, dosage and batch (lot) are used automatically for the label — you only need to describe the vial, cap and contents.
                            </p>

                            <div className="space-y-3">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                    <div>
                                        <label className="block text-[10px] font-mono uppercase tracking-widest text-[var(--text-muted)] mb-1">Vial glass</label>
                                        <input
                                            value={regen.vial_glass}
                                            onChange={(e) => setRegen({ ...regen, vial_glass: e.target.value })}
                                            placeholder="e.g. amber pharmaceutical glass, clear glass, cobalt blue glass"
                                            className="w-full px-3.5 py-2 bg-black/40 border border-white/10 rounded-lg text-sm text-white placeholder:text-[var(--text-muted)] focus:outline-none focus:border-[var(--tiffany)]"
                                            data-testid="regen-vial-glass"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-[10px] font-mono uppercase tracking-widest text-[var(--text-muted)] mb-1">Crimp collar</label>
                                        <input
                                            value={regen.crimp}
                                            onChange={(e) => setRegen({ ...regen, crimp: e.target.value })}
                                            placeholder="e.g. brushed silver aluminium, matte gold, polished copper"
                                            className="w-full px-3.5 py-2 bg-black/40 border border-white/10 rounded-lg text-sm text-white placeholder:text-[var(--text-muted)] focus:outline-none focus:border-[var(--tiffany)]"
                                            data-testid="regen-crimp"
                                        />
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-[10px] font-mono uppercase tracking-widest text-[var(--text-muted)] mb-1">Cap colour</label>
                                    <input
                                        value={regen.cap_colour}
                                        onChange={(e) => setRegen({ ...regen, cap_colour: e.target.value })}
                                        placeholder="e.g. matte black, deep forest green, vivid yellow"
                                        className="w-full px-3.5 py-2 bg-black/40 border border-white/10 rounded-lg text-sm text-white placeholder:text-[var(--text-muted)] focus:outline-none focus:border-[var(--tiffany)]"
                                        data-testid="regen-cap-colour"
                                    />
                                </div>
                                <div>
                                    <label className="block text-[10px] font-mono uppercase tracking-widest text-[var(--text-muted)] mb-1">Contents description</label>
                                    <textarea
                                        value={regen.contents}
                                        onChange={(e) => setRegen({ ...regen, contents: e.target.value })}
                                        rows={2}
                                        className="w-full px-3.5 py-2 bg-black/40 border border-white/10 rounded-lg text-sm text-white placeholder:text-[var(--text-muted)] focus:outline-none focus:border-[var(--tiffany)] resize-y"
                                        placeholder="e.g. fine white lyophilised powder filling the bottom ~30% of the vial"
                                        data-testid="regen-contents"
                                    />
                                </div>
                                <div>
                                    <label className="block text-[10px] font-mono uppercase tracking-widest text-[var(--text-muted)] mb-1">Extra notes (optional)</label>
                                    <input
                                        value={regen.extra_notes}
                                        onChange={(e) => setRegen({ ...regen, extra_notes: e.target.value })}
                                        placeholder="Anything else you want the generator to know"
                                        className="w-full px-3.5 py-2 bg-black/40 border border-white/10 rounded-lg text-sm text-white placeholder:text-[var(--text-muted)] focus:outline-none focus:border-[var(--tiffany)]"
                                        data-testid="regen-extra-notes"
                                    />
                                </div>
                            </div>

                            <div className="flex gap-2 mt-5">
                                <button
                                    onClick={submitRegen}
                                    disabled={regen.submitting}
                                    className="btn-solid flex-1 disabled:opacity-50"
                                    data-testid="regen-submit"
                                >
                                    <Sparkles size={14} />
                                    {regen.submitting ? "Generating…" : "Regenerate photo"}
                                </button>
                                <button
                                    onClick={() => !regen.submitting && setRegen(null)}
                                    disabled={regen.submitting}
                                    className="btn-ghost"
                                    data-testid="regen-cancel"
                                >
                                    Cancel
                                </button>
                            </div>
                            <p className="text-[10px] font-mono text-[var(--text-muted)] mt-3">
                                ~10–20s per image. The existing photo is auto-backed up first and only overwritten if the new image passes validation (≥ 400×400, valid PNG).
                            </p>

                            {/* Restore previous backups */}
                            <div className="mt-5 pt-4 border-t border-white/5">
                                <div className="flex items-center justify-between mb-3">
                                    <p className="text-[10px] font-mono uppercase tracking-widest text-[var(--text-muted)]">Previous photos</p>
                                    <button
                                        onClick={loadRegenBackups}
                                        className="text-[10px] font-mono uppercase tracking-widest text-[var(--tiffany)] hover:text-white"
                                        data-testid="regen-load-backups"
                                        disabled={regen.loadingBackups}
                                    >
                                        {regen.loadingBackups ? "Loading…" : (regen.backups.length ? "Refresh" : "Show backups")}
                                    </button>
                                </div>
                                {regen.backups.length === 0 ? (
                                    <p className="text-xs text-[var(--text-muted)]">Click &ldquo;Show backups&rdquo; to list restorable versions.</p>
                                ) : (
                                    <ul className="space-y-1.5 max-h-40 overflow-y-auto" data-testid="regen-backups-list">
                                        {regen.backups.map((b) => (
                                            <li key={b.index} className="flex items-center gap-2 text-xs">
                                                <span className="flex-1 text-white/80 truncate font-mono">
                                                    {b.ts?.slice(0, 19).replace("T", " ")} · {b.width}×{b.height}
                                                </span>
                                                <button
                                                    onClick={() => restoreBackup(b.index)}
                                                    disabled={regen.submitting}
                                                    className="px-2.5 py-1 rounded-full border border-white/10 text-[var(--gold)] hover:border-[var(--gold)] disabled:opacity-50 text-[10px] font-mono uppercase tracking-widest"
                                                    data-testid={`regen-restore-${b.index}`}
                                                >
                                                    Restore
                                                </button>
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </div>
                        </div>
                    </div>
                </ModalPortal>
            )}
        </div>
    );
};


const ProductImageManager = ({ slug, initialImages, initialPrimary, headers, onChange }) => {
    const [images, setImages] = useState(initialImages || []);
    const [primary, setPrimary] = useState(initialPrimary || "");
    const [busy, setBusy] = useState(false);
    const fileRef = useRef(null);

    // Push updated state to parent so it saves alongside the rest of the form
    const push = (imgs, prim) => {
        setImages(imgs);
        setPrimary(prim);
        onChange?.(imgs, prim);
    };

    const handleUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        if (file.size > 2 * 1024 * 1024) {
            toast.error("Image too large (max ~1.5 MB after encoding). Try a JPEG under 1.5 MB.");
            return;
        }
        setBusy(true);
        try {
            const b64 = await new Promise((resolve, reject) => {
                const r = new FileReader();
                r.onload = () => resolve(r.result);
                r.onerror = reject;
                r.readAsDataURL(file);
            });
            const res = await api.post(`/admin/products/${slug}/images`, { image_b64: b64, filename: file.name }, { headers });
            const newImages = [...images, b64];
            const newPrimary = images.length === 0 ? b64 : primary;
            push(newImages, newPrimary);
            toast.success(`Image #${res.data.index + 1} uploaded`);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Upload failed");
        }
        setBusy(false);
        if (fileRef.current) fileRef.current.value = "";
    };

    const removeAt = async (i) => {
        setBusy(true);
        try {
            await api.post(`/admin/products/${slug}/images/remove`, { index: i }, { headers });
            const removed = images[i];
            const next = images.filter((_, idx) => idx !== i);
            let nextPrimary = primary;
            if (removed === primary) nextPrimary = next[0] || "";
            push(next, nextPrimary);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Delete failed");
        }
        setBusy(false);
    };

    const setAsPrimary = async (i) => {
        setBusy(true);
        try {
            await api.post(`/admin/products/${slug}/images/primary`, { index: i }, { headers });
            push(images, images[i]);
            toast.success("Primary image updated");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Set primary failed");
        }
        setBusy(false);
    };

    return (
        <div className="mb-5 p-4 rounded-2xl border border-white/10 bg-black/20" data-testid="product-image-manager">
            <div className="flex items-center justify-between mb-3">
                <p className="text-[10px] font-mono uppercase tracking-[0.25em] text-[var(--text-muted)] flex items-center gap-1.5">
                    <ImageIcon size={11} /> Product images ({images.length})
                </p>
                <label className="btn-neon cursor-pointer" data-testid="product-image-upload">
                    <Upload size={14} /> {busy ? "Uploading…" : "Upload image"}
                    <input
                        ref={fileRef}
                        type="file"
                        accept="image/*"
                        onChange={handleUpload}
                        disabled={busy}
                        className="hidden"
                    />
                </label>
            </div>
            {images.length === 0 ? (
                <p className="text-xs text-[var(--text-muted)] italic">No images yet. Upload your first product photo.</p>
            ) : (
                <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                    {images.map((img, i) => {
                        const isPrimary = img === primary;
                        return (
                            <div
                                key={i}
                                className={`relative rounded-lg overflow-hidden border ${
                                    isPrimary ? "border-[var(--gold)] ring-1 ring-[var(--gold)]/50" : "border-white/10"
                                }`}
                                style={{ aspectRatio: "3 / 2" }}
                                data-testid={`product-image-${i}`}
                            >
                                <img src={img} alt={`Product ${i + 1}`} className="w-full h-full object-contain" />
                                {isPrimary && (
                                    <div className="absolute top-1 left-1 px-1.5 py-0.5 rounded-full bg-black/70 text-[8px] font-mono uppercase tracking-widest text-[var(--gold)] flex items-center gap-1">
                                        <Star size={8} /> Primary
                                    </div>
                                )}
                                <div className="absolute inset-x-0 bottom-0 flex justify-center gap-1 p-1 bg-gradient-to-t from-black/90 to-transparent opacity-0 hover:opacity-100 transition-opacity">
                                    {!isPrimary && (
                                        <button
                                            onClick={() => setAsPrimary(i)}
                                            className="w-7 h-7 rounded-full bg-[var(--gold)]/20 border border-[var(--gold)]/40 flex items-center justify-center hover:bg-[var(--gold)]/30"
                                            title="Set as primary"
                                            data-testid={`product-image-primary-${i}`}
                                        >
                                            <Star size={11} className="text-[var(--gold)]" />
                                        </button>
                                    )}
                                    <button
                                        onClick={() => removeAt(i)}
                                        className="w-7 h-7 rounded-full bg-red-500/20 border border-red-500/40 flex items-center justify-center hover:bg-red-500/30"
                                        title="Remove image"
                                        data-testid={`product-image-remove-${i}`}
                                    >
                                        <Trash2 size={11} className="text-red-300" />
                                    </button>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};


/* ---------------------------------------------------------------------------
 * ImageDisplayControls
 * ---------------------------------------------------------------------------
 * Coarse presets (Fit / Fill / Zoom-in / Zoom-out) + 0-100% X/Y sliders so
 * admins can position portrait uploads inside the standard 3:2 landscape
 * listing container without regenerating the asset. Preview mirrors the
 * exact <ProductImage/> the storefront renders, so what admins see IS what
 * shoppers see.
 *
 * "Reset to Listing Preset" restores the Carbon Labs Product Listing Standard
 * defaults: fit=contain, zoom=1×, offset=(50%, 50%).
 * -------------------------------------------------------------------------*/
const FIT_OPTIONS = [
    { key: "contain",  label: "Fit",       hint: "Whole product visible" },
    { key: "cover",    label: "Fill",      hint: "Fill frame, may crop edges" },
    { key: "zoom-in",  label: "Zoom In",   hint: "1.2× internal scale" },
    { key: "zoom-out", label: "Zoom Out",  hint: "0.85× internal scale" },
];

const ImageDisplayControls = ({ form, setForm }) => {
    const preview = {
        image: form.image,
        name: form.name || "Preview",
        slug: form.slug || "preview",
        image_fit: form.image_fit,
        image_zoom: form.image_zoom,
        image_offset_x: form.image_offset_x,
        image_offset_y: form.image_offset_y,
    };

    const reset = () => {
        setForm((f) => ({
            ...f,
            image_fit: "contain",
            image_zoom: 1.0,
            image_offset_x: 50,
            image_offset_y: 50,
        }));
    };

    return (
        <div
            className="mb-5 rounded-2xl border border-white/5 bg-black/20 p-4 space-y-4"
            data-testid="image-display-controls"
        >
            <div className="flex items-center justify-between flex-wrap gap-2">
                <div>
                    <div className="text-sm font-display font-bold text-white">Listing image display</div>
                    <div className="text-[11px] text-[var(--text-muted)] mt-0.5">
                        Fine-tune how this product renders inside the standard landscape listing container.
                    </div>
                </div>
                <button
                    type="button"
                    onClick={reset}
                    className="btn-neon !py-1.5 !px-3 !text-[10px]"
                    data-testid="image-controls-reset"
                    title="Carbon Labs Product Listing Standard"
                >
                    <RefreshCw size={12} /> Reset to Listing Preset
                </button>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {/* Live preview using the SAME component the storefront uses */}
                <div>
                    <div className="text-[10px] font-mono uppercase tracking-widest text-[var(--text-muted)] mb-1.5">
                        Live preview · 3:2 landscape card
                    </div>
                    <div className="rounded-xl border border-[var(--gold)]/25 bg-gradient-to-b from-[#15181B] to-[#0B0D0F] overflow-hidden">
                        <ProductImage
                            product={preview}
                            ratio="3 / 2"
                            loading="eager"
                            data-testid="image-controls-preview"
                        />
                    </div>
                </div>

                {/* Controls */}
                <div className="space-y-4">
                    {/* Fit presets */}
                    <div>
                        <div className="text-[10px] font-mono uppercase tracking-widest text-[var(--text-muted)] mb-1.5">
                            Fit preset
                        </div>
                        <div className="grid grid-cols-4 gap-2">
                            {FIT_OPTIONS.map((opt) => {
                                const active = (form.image_fit || "contain") === opt.key;
                                return (
                                    <button
                                        key={opt.key}
                                        type="button"
                                        onClick={() => setForm((f) => ({ ...f, image_fit: opt.key }))}
                                        title={opt.hint}
                                        className={`px-2 py-2 rounded-lg text-[11px] font-mono uppercase tracking-wider border transition-all ${
                                            active
                                                ? "border-[var(--gold)] text-[var(--gold)] bg-[rgba(200,155,74,0.1)]"
                                                : "border-white/10 text-[var(--text-secondary)] hover:border-white/25"
                                        }`}
                                        data-testid={`image-fit-${opt.key}`}
                                    >
                                        {opt.label}
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    {/* Zoom slider — coarse but with 4 stops */}
                    <div>
                        <div className="flex items-center justify-between mb-1.5">
                            <div className="text-[10px] font-mono uppercase tracking-widest text-[var(--text-muted)]">
                                Zoom
                            </div>
                            <div className="text-[11px] font-mono text-white">
                                {Number(form.image_zoom ?? 1).toFixed(2)}×
                            </div>
                        </div>
                        <input
                            type="range"
                            min="0.5"
                            max="2"
                            step="0.05"
                            value={form.image_zoom ?? 1}
                            onChange={(e) => setForm((f) => ({ ...f, image_zoom: Number(e.target.value) }))}
                            className="w-full accent-[var(--gold)]"
                            data-testid="image-zoom-slider"
                        />
                    </div>

                    {/* X offset */}
                    <div>
                        <div className="flex items-center justify-between mb-1.5">
                            <div className="text-[10px] font-mono uppercase tracking-widest text-[var(--text-muted)]">
                                Horizontal position
                            </div>
                            <div className="text-[11px] font-mono text-white">{form.image_offset_x ?? 50}%</div>
                        </div>
                        <input
                            type="range"
                            min="0"
                            max="100"
                            step="1"
                            value={form.image_offset_x ?? 50}
                            onChange={(e) => setForm((f) => ({ ...f, image_offset_x: Number(e.target.value) }))}
                            className="w-full accent-[var(--gold)]"
                            data-testid="image-offset-x-slider"
                        />
                    </div>

                    {/* Y offset */}
                    <div>
                        <div className="flex items-center justify-between mb-1.5">
                            <div className="text-[10px] font-mono uppercase tracking-widest text-[var(--text-muted)]">
                                Vertical position
                            </div>
                            <div className="text-[11px] font-mono text-white">{form.image_offset_y ?? 50}%</div>
                        </div>
                        <input
                            type="range"
                            min="0"
                            max="100"
                            step="1"
                            value={form.image_offset_y ?? 50}
                            onChange={(e) => setForm((f) => ({ ...f, image_offset_y: Number(e.target.value) }))}
                            className="w-full accent-[var(--gold)]"
                            data-testid="image-offset-y-slider"
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};
